import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import { IconCancel } from 'components/Icons';
import './MobileSlideUpPanel.scss';

/**
 * MobileSlideUpPanel - Gesture-based slide-up panel for mobile
 *
 * Features:
 * - Drag-to-resize: Swipe up to expand, swipe down to close
 * - Velocity detection: Fast swipe down closes, fast swipe up maximizes
 * - Configurable heights and behavior
 *
 * @see Original: src/presentation/components/Mobile/MobileSlideUpPanel
 */

interface MobileSlideUpPanelProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  titleIcon?: React.ReactNode;
  showGrabHandle?: boolean;
  showExplicitCloseButton?: boolean;
  showBetaBadge?: boolean;
  initialHeight?: number;
  minHeight?: number;
  maxHeight?: number;
  resizable?: boolean;
  fullSizeOnOpen?: boolean;
  useFitContent?: boolean;
}

export const MobileSlideUpPanel: React.FC<MobileSlideUpPanelProps> = React.memo(
  ({
    isOpen,
    onClose,
    children,
    title,
    titleIcon,
    showGrabHandle = true,
    showExplicitCloseButton = true,
    showBetaBadge = false,
    initialHeight = 300,
    minHeight = 150,
    maxHeight: propMaxHeight,
    resizable = true,
    fullSizeOnOpen = false,
    useFitContent = true,
  }) => {
    const prefix = prefixClass('ksq2-mobile-slide-up-panel');

    // Dynamically calculate maxHeight based on window size
    const [calculatedMaxHeight, setCalculatedMaxHeight] = useState(window.innerHeight - 50);

    // Use provided maxHeight or calculated one
    const maxHeight = propMaxHeight ?? calculatedMaxHeight;

    // Track if component should be in DOM
    const [isInDOM, setIsInDOM] = useState(isOpen);
    // Track animation state separately from isOpen prop
    const [isAnimatingIn, setIsAnimatingIn] = useState(false);
    // Track panel height for resizing
    const [panelHeight, setPanelHeight] = useState(fullSizeOnOpen ? maxHeight : initialHeight);
    // Track if currently dragging
    const [isDragging, setIsDragging] = useState(false);
    // Store the starting point for drag calculations
    const [dragStartY, setDragStartY] = useState(0);
    const [startHeight, setStartHeight] = useState(initialHeight);
    // Track velocity for gesture detection
    const [lastY, setLastY] = useState(0);
    const [lastTime, setLastTime] = useState(0);
    const [velocity, setVelocity] = useState(0);

    // Reference to the panel content
    const panelRef = useRef<HTMLDivElement>(null);

    // Memoize panel content class
    const panelContentClass = useMemo(() => {
      return classNames(prefix('__content'), {
        'is-open': isAnimatingIn,
        'is-dragging': isDragging,
      });
    }, [prefix, isAnimatingIn, isDragging]);

    // Initialize animation state based on isOpen prop
    useEffect(() => {
      if (isOpen) {
        setIsInDOM(true);
        const timer = setTimeout(() => {
          setIsAnimatingIn(true);
        }, 10);
        return () => clearTimeout(timer);
      }
    }, []);

    // Handle window resize to update maxHeight
    useEffect(() => {
      const handleResize = () => {
        setCalculatedMaxHeight(window.innerHeight - 50);

        if (panelHeight > window.innerHeight - 50) {
          setPanelHeight(window.innerHeight - 50);
        }
      };

      window.addEventListener('resize', handleResize);
      window.addEventListener('orientationchange', handleResize);

      handleResize();

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('orientationchange', handleResize);
      };
    }, [panelHeight]);

    // Handle opening and closing animations
    useEffect(() => {
      if (isOpen) {
        setIsInDOM(true);
        const timer = setTimeout(() => {
          setIsAnimatingIn(true);
        }, 20);
        return () => clearTimeout(timer);
      } else {
        setIsAnimatingIn(false);
        setPanelHeight(fullSizeOnOpen ? maxHeight : initialHeight);

        const timer = setTimeout(() => {
          setIsInDOM(false);
        }, 300);
        return () => clearTimeout(timer);
      }
    }, [isOpen, initialHeight, fullSizeOnOpen, maxHeight]);

    const handleOverlayClick = useCallback(() => {
      onClose();
    }, [onClose]);

    // Handle touch events for dragging
    const handleTouchStart = useCallback(
      (e: React.TouchEvent) => {
        if (!resizable) return;

        try {
          if (e.touches.length === 0) return;

          const touchY = e.touches[0].clientY;

          setIsDragging(true);
          setDragStartY(touchY);
          setStartHeight(panelHeight);

          setLastY(touchY);
          setLastTime(Date.now());
          setVelocity(0);

          e.preventDefault();
        } catch (error) {
          console.error('Error in handleTouchStart:', error);
          setIsDragging(false);
        }
      },
      [resizable, panelHeight]
    );

    const handleTouchMove = useCallback(
      (e: React.TouchEvent) => {
        if (!isDragging || !resizable) return;

        try {
          if (e.touches.length === 0) return;

          const currentY = e.touches[0].clientY;
          const currentTime = Date.now();
          const deltaY = dragStartY - currentY;
          let newHeight = startHeight + deltaY;

          if (currentTime - lastTime > 0) {
            const instantVelocity = (lastY - currentY) / (currentTime - lastTime);
            setVelocity(prev => prev * 0.7 + instantVelocity * 0.3);
          }

          setLastY(currentY);
          setLastTime(currentTime);

          newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));

          requestAnimationFrame(() => {
            setPanelHeight(newHeight);
          });

          e.preventDefault();
        } catch (error) {
          console.error('Error in handleTouchMove:', error);
          setIsDragging(false);
        }
      },
      [isDragging, resizable, dragStartY, startHeight, lastTime, lastY, minHeight, maxHeight]
    );

    const handleTouchEnd = useCallback(() => {
      if (!resizable) return;

      try {
        setIsDragging(false);

        const VELOCITY_THRESHOLD = 0.5;

        if (velocity < -VELOCITY_THRESHOLD) {
          onClose();
          return;
        } else if (velocity > VELOCITY_THRESHOLD) {
          setPanelHeight(maxHeight);
          return;
        }

        if (panelHeight < initialHeight * 0.8 || panelHeight < minHeight * 0.7) {
          onClose();
        } else if (panelHeight > initialHeight * 1.5) {
          setPanelHeight(maxHeight);
        } else {
          setPanelHeight(initialHeight);
        }
      } catch (error) {
        console.error('Error in handleTouchEnd:', error);
        setPanelHeight(initialHeight);
      }
    }, [resizable, velocity, panelHeight, initialHeight, minHeight, maxHeight, onClose]);

    const handleGrabHandleClick = useCallback(
      (e: React.MouseEvent) => {
        if (!isDragging) {
          onClose();
        }
        e.preventDefault();
      },
      [isDragging, onClose]
    );

    const handleExplicitCloseClick = useCallback(() => {
      onClose();
    }, [onClose]);

    const handlePanelContentClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    if (!isInDOM) {
      return null;
    }

    return (
      <div
        className={classNames(prefix('__overlay'), { 'is-open': isAnimatingIn })}
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal={isOpen ? 'true' : undefined}
        aria-labelledby={title ? 'slide-up-panel-title' : undefined}
      >
        <div
          ref={panelRef}
          className={panelContentClass}
          onClick={handlePanelContentClick}
          style={{
            minHeight: useFitContent ? `${panelHeight}px` : `${minHeight}px`,
            maxHeight: `${maxHeight}px`,
          }}
        >
          {showGrabHandle && (
            <div
              className={prefix('__grab-handle')}
              onClick={handleGrabHandleClick}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />
          )}
          {(title || titleIcon || showExplicitCloseButton) && (
            <div
              className={classNames(prefix('__header'), {
                [prefix('__header--center')]: !showExplicitCloseButton,
              })}
            >
              <div className={prefix('__title-container')}>
                {titleIcon && <div className={prefix('__icon')}>{titleIcon}</div>}
                <div className={prefix('__title-wrapper')}>
                  {title && (
                    <h3 id="slide-up-panel-title" className={prefix('__title')}>
                      {title}
                    </h3>
                  )}
                  {showBetaBadge && <span className={prefix('__beta-badge')}>BETA</span>}
                </div>
              </div>
              {showExplicitCloseButton && (
                <button
                  type="button"
                  className={prefix('__close-button')}
                  onClick={handleExplicitCloseClick}
                  aria-label="Close panel"
                >
                  <IconCancel />
                </button>
              )}
            </div>
          )}
          <div className={prefix('__body')}>{children}</div>
        </div>
      </div>
    );
  }
);

MobileSlideUpPanel.displayName = 'MobileSlideUpPanel';

export default MobileSlideUpPanel;
